/// <amd-module name="Test/AttachmentsView/Actions/AttachmentsViewActionsCreator" />
import { AttachmentsViewActionsHub } from "Test/AttachmentsView/Actions/AttachmentsViewActionsHub";
import { AttachmentModelType, ISelectedAttachment, AttachmentType } from "Test/AttachmentsView/Attachment";
import { AttachmentsLogStoreViewSource } from "Test/AttachmentsView/Source/AttachmentsLogStoreViewSource";
import { AttachmentsViewSource } from "Test/AttachmentsView/Source/AttachmentsViewSource";
import * as TelemetryConstants from "Test/AttachmentsView/TelemetryConstants";
import { TestAttachmentModel } from "Test/AttachmentsView/TestAttachmentModel";
import { TestLogListItem } from "Test/common-util/Contracts/ExtendedTest";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import * as Telemetry from "Test/common-util/Telemetry";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPerformanceService } from "VSS/Platform/Performance";

export class AttachmentsViewActionsCreator {
    private static LogStoreAttachmentsFeatureFlag: string = "WebAccess.TestManagement.LogStore";

    constructor(private _pageContext: IVssPageContext, private _actionsHub: AttachmentsViewActionsHub, private _source: AttachmentsViewSource, private _logStoreSource: AttachmentsLogStoreViewSource) {
    }

    public setDefaultState(): void {
        this._actionsHub.setDefaultState.invoke(null);
    }

    public getAttachments(testRunId: number, testResultId: number, subResultId: number): void {
        // Removed as part of attachment UI compat PR - 528779
        // The legacy call gets the LS attachments as well as FS attachments, we are handling the size limit via handling LS
        // downloads differently
        this._source.getAttachments(this._pageContext, testRunId, testResultId, subResultId).then((attachments: TCMContracts.TestAttachment[]) => {
                this._loadAttachments(attachments);
        }, (error) => {
            this._logError(error);
        });
    }

    public downloadSelectedAttachments(attachments: TCMContracts.TestAttachment[]): void {
        attachments.forEach((attachment, index) => {
            this._launchUrl(attachment.url);
        });

        Telemetry.publishDetailsPaneEvents(this._pageContext, null, false, TelemetryConstants.featureTestTab_AttachmentDownloadedCount, { "count": attachments.length });
    }

    public downloadSelectedLogStoreAttachments(runId: number, resultId: number, subResultId:number, filePath:string, backupUri: string): void {
        this._logStoreSource.getTestLogStoreEndpointDetailsForAttachmentNonObject(this._pageContext, runId, resultId, subResultId, TCMContracts.TestLogType.GeneralAttachment, filePath).then(
            (endpoint: TCMContracts.TestLogStoreEndpointDetails) => {
                if (endpoint.status == 1 || (!!endpoint.endpointSASUri && endpoint.endpointSASUri.length == 0)){
                    this._launchUrl(backupUri);
                } 
                else{
                    this._launchUrl(endpoint.endpointSASUri);
                }
            },
            (error: any) => {
                // It may be that we are getting exception because attachment is in File System
                // Because we are getting all attachments (file system and logstore one) with the help of 
                // file system api.
                this._launchUrl(backupUri);
            }
        )
        Telemetry.publishDetailsPaneEvents(this._pageContext, null, false, TelemetryConstants.featureTestTab_AttachmentDownloadedCount, { "count": 1 });
    }

    public showAttachmentPreview(
        attachmentId: number,
        testRunId: number,
        testResultId: number,
        subResultId: number): void {
        this._source.loadAttachmentContent(this._pageContext, attachmentId, testRunId, testResultId, subResultId).then((attachmentContent: ArrayBuffer) => {
            this._parseContentBuffer(attachmentContent, (result: string) => {
                this._actionsHub.showAttachmentPreview.invoke(result);
            });
        }, (error) => {
            this._actionsHub.onErrorInPreview.invoke(error.info || error.message || error.toString());
        });
    }

    public showLogstoreAttachmentPreview(runId: number, resultId: number, subResultId:number, filePath:string, size: number, id: number): void {
        this._logStoreSource.getTestLogStoreEndpointDetailsForAttachmentNonObject(this._pageContext, runId, resultId, subResultId, TCMContracts.TestLogType.GeneralAttachment, filePath).then(
            (endpoint: TCMContracts.TestLogStoreEndpointDetails) => {
            if (endpoint.status == 1 || (!!endpoint.endpointSASUri && endpoint.endpointSASUri.length == 0)){
                this.showAttachmentPreview(id, runId, resultId, subResultId);
            }
            // Setting this limit because there is no limit of attachment size on log store.
            let attachmentSizeInMB = size / this._numberOfBytesPerMB;
            if (attachmentSizeInMB < this._previewSizeLimitInMB) {
                this.setAttachmentContentLoading();
                const performanceService: IVssPerformanceService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                performanceService.startScenario(TelemetryConstants.featureTestTab_PerfLogStoreAttachment, false);
                this._logStoreSource.loadAttachmentContent(endpoint.endpointSASUri).then((attachmentContent: ArrayBuffer) => {
                    this._parseContentBuffer(attachmentContent, (result: string) => {
                        this._actionsHub.showAttachmentPreview.invoke(result);
                        performanceService.endScenario(TelemetryConstants.TestTab_LogStoreAttachmentAreaName, TelemetryConstants.featureTestTab_PerfLogStoreAttachment, false, undefined, { "Status" : "Success", "RunId": runId, "ResultId": resultId, "SubResultId": subResultId });
                    });
                }, (error) => {
                    this._actionsHub.onErrorInPreview.invoke(error.info || error.message || error.toString());
                    performanceService.endScenario(
                        TelemetryConstants.TestTab_LogStoreAttachmentAreaName,
                        TelemetryConstants.featureTestTab_PerfLogStoreAttachment,
                        false,
                        undefined,
                        { "Status" : "Failed", "Error": error.info || error.message || error.toString(), "RunId": runId, "ResultId": resultId, "SubResultId": subResultId });
                });
            }
            else{
                this._actionsHub.setLastSelectedAttachmentUnpreviewable.invoke(null)
            }
        },
        (error: any) => {
            this._actionsHub.onErrorInPreview.invoke(error.info || error.message || error.toString());
        })
    }

    public updateContextMenuOpenIndex(openIndex: number): void {
        this._actionsHub.updateContextMenuOpenIndex.invoke(openIndex);
    }

    public updateOnlyPreviewModeSelection(currentPreviewMode: boolean): void {
        this._actionsHub.updateCurrentAttachmentPreviewMode.invoke(currentPreviewMode);
    }

    public onActiveItemChanged(attachment: TCMContracts.TestAttachment | TestLogListItem, attachmentModelType: AttachmentModelType, attachmentType: AttachmentType, testRunId: number, testResultId: number, subResultId: number) {
        if (attachmentModelType === AttachmentModelType.Legacy) {
            let legacyAttachment = attachment as TCMContracts.TestAttachment;
            let lastSelectedAttachment: ISelectedAttachment = {
                attachment: legacyAttachment,
                filePath: legacyAttachment.fileName,
                url: legacyAttachment.url,
                attachmentType: attachmentModelType
            }
            this._actionsHub.setLastSelectedAttachment.invoke(lastSelectedAttachment);

            // If attachment is previewable, then preview the content
            if (attachmentType === AttachmentType.previewableText) {
                if (attachmentModelType === AttachmentModelType.Legacy) {
                    this._actionsHub.setAttachmentContentLoading.invoke(null);
                    if(this._isItemInLogstore((attachment as TCMContracts.TestAttachment).id)){
                        this.showLogstoreAttachmentPreview(
                            testRunId,
                            testResultId,
                            subResultId,
                            (attachment as TCMContracts.TestAttachment).fileName,
                            (attachment as TCMContracts.TestAttachment).size,
                            (attachment as TCMContracts.TestAttachment).id
                        )
                    }
                    else{
                        this.showAttachmentPreview(
                            (attachment as TCMContracts.TestAttachment).id,
                            testRunId,
                            testResultId,
                            subResultId
                        );
                    }
                }
            }
        }
        else {
            let logStoreAttachment = attachment as TestLogListItem;
            this._logStoreSource.getTestLogStoreEndpointDetailsForAttachment(this._pageContext, logStoreAttachment).then(
                (endpoint: TCMContracts.TestLogStoreEndpointDetails) => {
                    let lastSelectedAttachment: ISelectedAttachment = {
                        attachment: logStoreAttachment,
                        filePath: logStoreAttachment.testLog.logReference.filePath,
                        url: endpoint.endpointSASUri,
                        attachmentType: attachmentModelType
                    }
                    this._actionsHub.setLastSelectedAttachment.invoke(lastSelectedAttachment);
                }
            )
        }
    }

    public setAttachmentContentLoading(): void {
        this._actionsHub.setAttachmentContentLoading.invoke(null);
    }

    public closeErrorMessage(): void {
        this._actionsHub.closeErrorMessage.invoke(null);
    }

    private _logError(error: any): void {
        this._actionsHub.onError.invoke(error.info || error.message || error.toString());
    }

    private _sendAttachmentFileExtensionTypeTelemetry(attachments: TCMContracts.TestAttachment[]): void {
        // Send telemetry for counts of each attachment type
        if (attachments.length > 0) {
            let fileNameExtensionDictionary: { [id: string]: number; } = {};
            attachments.forEach(attachment => {
                const fileExtension: string = this._getFilenameExtension(attachment.fileName);
                fileNameExtensionDictionary[fileExtension] = fileNameExtensionDictionary[fileExtension]
                    ? fileNameExtensionDictionary[fileExtension] + 1
                    : 1;
            });

            Telemetry.publishEvent(this._pageContext, null, TelemetryConstants.featureTestTab_AttachmentsExtensionType, fileNameExtensionDictionary);
        }

        // Telemetry for attachment count and if it is for test results or test run
        Telemetry.publishEvent(this._pageContext, null, TelemetryConstants.featureTestTab_AttachmentsCount, {
            "count": attachments.length
        });
    }

    private _getFilenameExtension(filename: string): string {
        let fileNameExtension: string = "";
        if (filename.indexOf(".") !== -1) {
            fileNameExtension = filename.substring(filename.lastIndexOf("."));
        }
        return fileNameExtension;
    }

    private _parseContentBuffer(content: ArrayBuffer, callback: any): void {
        let readChunk = null;

        const readEventHandler = (evt: any) => {
            if (evt.target.error === null) {
                callback(evt.target.result);
                return;
            }
        };

        readChunk = (_content: ArrayBuffer) => {
            const reader = new FileReader();
            const blob = new Blob([new Uint8Array(_content)]);
            reader.onload = readEventHandler;
            reader.readAsText(blob);
        };

        readChunk(content);
    }

    private _loadAttachments(attachments: TCMContracts.TestAttachment[]) {
        const attachmentsOM = new TestAttachmentModel();
        attachmentsOM.setLegacyAttachments(attachments);
        this._actionsHub.attachmentsLoaded.invoke(attachmentsOM);
        if (!!attachmentsOM.getLegacyAttachments()[0])
        {
            const legacyAttachment = attachmentsOM.getLegacyAttachments()[0];
            let lastSelectedAttachment: ISelectedAttachment = {
                attachment: legacyAttachment,
                filePath: legacyAttachment.fileName,
                url: legacyAttachment.url,
                attachmentType: AttachmentModelType.Legacy
            }
            this._actionsHub.setLastSelectedAttachment.invoke(lastSelectedAttachment);
        }   
        // Send telemetry
        this._sendAttachmentFileExtensionTypeTelemetry(attachments);
    }

    private _launchUrl(url: string): void {
        let openedWindow = window.open(url, "_blank");
        if (openedWindow) {
            openedWindow.opener = null;
        }
    }

    private _isItemInLogstore = (id: number): boolean => {
        // Odd ids are from LS; Even Ids are from FS
        if (!isFeatureFlagEnabled(this._pageContext, this._previewLogStoreAttachmentRunFF, false)) {
            return false;
        }
        else {
            if (id % 2 != 0) {
                return true;
            }
            else {
                return false;
            }
        }
    }

    private _previewLogStoreAttachmentRunFF: string = "WebAccess.TestManagement.PreviewLogStoreAttachment";
    private _numberOfBytesPerMB: number = 1048576;  // 1024 * 1024 = 1MB (Size comes in byte)
    private _previewSizeLimitInMB: number = 100
}
